import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FavoriteButton from '../../atoms/FavoriteButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Link from '@mui/material/Link';
import { QueryParamProvider } from 'use-query-params';
import { ReachAdapter } from 'use-query-params/adapters/reach';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { useTranslation } from '../../hooks/useTranslatedField';
import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import Grid from '@mui/material/Unstable_Grid2';
import { createIntl, FormattedMessage, FormattedNumberParts, IntlProvider } from 'react-intl';
import ar from './ar.json';
import en from './en.json';
import { LocaleProvider } from '../../../contexts/language';
import CarImageGallery from '../../molecules/CarImageGallery';
import { ComparisonContext } from '../../../contexts/comparison';
import ShareItemButton from '../../atoms/ShareItemButton';
import CarTitle from '../../atoms/CarTitle';
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image';
import TrimCardFavorite from './trimCardFavorite';
import CarPriceBox from '../../atoms/CarPriceBox';
import { useCarLink } from '../../hooks/useCarLink';
import { filterSearchResults } from '../../../functions/filterSearchResults';
import allFields from '../../../config/allFields.json';
import { useSearchString } from '../../hooks/useSearchString';
import GetHelpPanel from '../../molecules/PureGetHelpPanel/GetHelpPanel';

const ModelFavoriteIcon = ({ slug }) => {
  const { addModel, removeModel, compareConfig } = React.useContext(ComparisonContext);
  return <FavoriteButton
    outlined
    onToggleFavorite={() => compareConfig?.models?.indexOf(slug) === -1 ? addModel(slug) : removeModel(slug)}
    favorite={compareConfig?.models?.indexOf(slug) !== -1}
  />;
};
ModelFavoriteIcon.propTypes = {
  slug: PropTypes.string.isRequired
};

const languages = {
  ar,
  en
};

const ModelPage = ({ pageContext: { lang, direction }, data }) => {
  const l = useLanguageLink();
  const t = useTranslation();
  const cl = useCarLink();
  const { searchParameters, url: searchURL, searchParamString } = useSearchString();
  const [viewAllResults, setViewAllResults] = React.useState(false);

  const hasSearchResults = searchURL?.length;
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });

  const useGatsbySortedImages = ({ images, name }) => {
    const elements = images?.sort((a, b) => a?.orderingPriority - b?.orderingPriority).map(({ localImage }, idx) =>
      <GatsbyImage
        key={idx}
        image={getImage(localImage)}
        layout="fixed"
        alt={t(name) || ''}
        height={200}
        style={{ width: '100%' }}
      />);
    if (!elements?.length) {
      return [
        <StaticImage
          key={1}
          src={'./images/modelPlaceholder.png'}
          layout="fixed"
          alt={`${name || ''}`}
          height={200}
          style={{ width: '100%' }}
        />
      ];
    }
    return elements;
  };
  const manufacturer = data.driveNinjaManufacturerType;
  const model = data.driveNinjaModelType;

  const allResults = data.allDriveNinjaModelType.edges.map(
    ({ node }) => node).map(
    ({ trimLevels, ...mdl }) => ({
      ...mdl,
      trimLevels: trimLevels.edges.map(
        ({ node }) => node).map(
        ({ offers, lifestyles, carTypes, carShapes, ...tl }) => ({
          ...tl,
          offers: offers.edges.map(
            ({ node }) => node
          ).filter(node => node).map(o => ({
            ...o,
            exteriorColor: o?.exteriorColor?.genericColor?.slug,
            interiorColor: o?.interiorColor?.genericColor?.slug
          })),
          lifestyles: lifestyles.edges.map(
            ({ node }) => node.slug
          ),
          carTypes: carTypes.edges.map(
            ({ node }) => node.slug
          ),
          carShapes: carShapes.edges.map(
            ({ node }) => node.slug
          )
        })
      )
    })
  );

  const searchResultData = hasSearchResults && !viewAllResults ? filterSearchResults({
    fieldLookups: {}, searchParameters, searchResults: allResults, allFields
  }) : allResults;

  const allTrimLevels = searchResultData.map(({ trimLevels }) => trimLevels).reduce((a, b) => [...a, ...b], []);

  const allOffers = allTrimLevels.map(tr => tr.offers.map(o => ({
    ...o,
    trimLevel: { slug: tr.slug, modelYear: tr.modelYear }
  }))).reduce((a, b) => [...a, ...b], []).filter(o => o?.monthlyAmount).sort((a, b) => a?.monthlyAmount - b?.monthlyAmount);

  const trimLevelYears = {};
  for (const t of allTrimLevels) {
    if (!trimLevelYears[t.modelYear]) {
      trimLevelYears[t.modelYear] = { year: t.modelYear, trimLevels: [] };
    }
    trimLevelYears[t.modelYear].trimLevels.push(t);
  }

  const rawTrimLevelYears = Object.values(trimLevelYears).sort((a, b) => parseFloat(a?.year) - parseFloat(b.year)).reverse();


  const toggleModelYearDisplay = (year) => {
    if (viewModelYears.indexOf(year) === -1) {
      setViewModelYears([...viewModelYears, year]);
    } else {
      setViewModelYears(viewModelYears.filter(y => y !== year));
    }
  };

  const bestNew = allOffers?.[0];
  const bestPriceLinkArgs = bestNew ? {
    href: '#newOffers',
    component: Link,
    sx: { textDecoration: 'none' },
    title: 'View Offers'
  } : {};

  const yearsWithOffers = rawTrimLevelYears.filter(({ trimLevels }) => trimLevels.filter(t => t.offers.length > 0).length > 0);
  const bestYear = bestNew?.trimLevel?.modelYear;

  const defaultYears = [yearsWithOffers?.[0]?.year || rawTrimLevelYears?.[0]?.year];
  if (bestYear) {
    defaultYears.push(bestYear);
  }
  const [viewModelYears, setViewModelYears] = React.useState(defaultYears);

  return (
    <LocaleProvider localeConfig={{ lang, direction }}>
      <DNGatsbyTemplate
        breadcrumbs={hasSearchResults ? [
          {
            name: intl.formatMessage({
              id: 'breaddcrumbs.SearchPage',
              defaultMessage: 'Search'
            }),
            path: searchURL
          },
          {
            name: t(manufacturer.translatedName),
            path: cl({ mfrSlug: manufacturer.slug })
          },
          {
            name: t(model.translatedName),
            path: cl({ mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
          }
        ] : [
          {
            name: intl.formatMessage({
              id: 'browseMake.BrowseMakeBreadcrumbs',
              defaultMessage: 'Browse by Manufacturer'
            }),
            path: l('/make/')
          },
          {
            name: t(manufacturer.translatedName),
            path: cl({ mfrSlug: manufacturer.slug })
          },
          {
            name: t(model.translatedName),
            path: cl({ mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
          }
        ]}
      >
        <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Grid container spacing={3}>
              <Grid xs={12} md={7} lg={8}>
                <CarImageGallery images={model.modelImages?.sort((a, b) => a.orderingPriority - b.orderingPriority)} />
              </Grid>
              <Grid xs={12} md={5} lg={4}>
                <Stack spacing={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CarTitle>{t(manufacturer.translatedName)} {t(model.translatedName)}</CarTitle>
                  </Box>
                  <Stack spacing={1} direction={'row'} sx={{ width: '100%' }}>
                    {bestNew ? (
                      <>
                        <CarPriceBox {...bestPriceLinkArgs}>
                          <Stack sx={{ justifyContent: 'space-between' }}>
                            <Box sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                              <FormattedMessage id={'NewOfferPage.financeNewFrom__amount'}
                                defaultMessage={'Monthly From'} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: '700', fontSize: '1.2rem' }} color={'primary'}>
                                <FormattedNumberParts
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                  style={'currency'}
                                  currency={bestNew.currency}
                                  value={bestNew.monthlyAmount}
                                >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ?
                                    <Box key={i} component="span" sx={{
                                      color: 'offerPricingCard.currency', fontSize: '1rem'
                                    }}>{value}</Box> : value)}
                                < /FormattedNumberParts>
                              </Typography>
                            </Box>
                          </Stack>
                        </CarPriceBox>
                        <CarPriceBox   {...bestPriceLinkArgs}>
                          <Stack sx={{ justifyContent: 'space-between' }}>
                            <Box sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                              <FormattedMessage id={'NewOfferPage.newFrom__amount'} defaultMessage={'Buy New from'} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: '700', fontSize: '1.2rem' }} color={'primary'}>
                                <FormattedNumberParts
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                  style={'currency'}
                                  currency={bestNew.currency}
                                  value={bestNew.purchaseAmount}
                                >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ?
                                    <Box key={i} component="span" sx={{
                                      color: 'offerPricingCard.currency', fontSize: '1rem'
                                    }}>{value}</Box> : value)}
                                < /FormattedNumberParts>
                              </Typography>
                            </Box>
                          </Stack>
                        </CarPriceBox>
                      </>
                    ) : null}

                  </Stack>
                  <Stack direction={'row'} spacing={1}>
                    <ModelFavoriteIcon slug={model.slug} />
                    <ShareItemButton
                      model={t(model.translatedName)}
                      make={t(manufacturer.translatedName)}
                      url={typeof window !== 'undefined' ? window.location.href : ''}
                    />
                  </Stack>

                </Stack>
              </Grid>
              <Grid xs={12}>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {model.translatedDescription.en?.length > 10 ? (
                    <Stack spacing={2}>
                      {t(model.translatedDescription).split('\n').map((para, k) =>
                        <Typography variant={'body1'} key={k}>{para}</Typography>)}
                    </Stack>

                  ) : null}
                  <a id={'newOffers'} />
                  <Typography variant={'h2'}>
                    <FormattedMessage
                      id={'modelPage.modelListTitle'}
                      defaultMessage={'Choose a Model'}
                    />
                  </Typography>

                  {!viewAllResults && hasSearchResults ? (
                    <>
                      <Typography variant={'body1'}>
                        <FormattedMessage
                          id={'modelPage.modelList.searchResultsExplainer'}
                          defaultMessage={'Below are the {model} models that match your search, from a total of {length} currently available. Tap on any model below to view more details, compare, or start building your deal now!'}
                          values={{
                            length: allResults.map(a => a.trimLevels.length).reduce((partialSum, a) => partialSum + a, 0),
                            model: t(model.translatedName)
                          }}
                        />
                      </Typography>
                      <Box sx={{
                        justifyContent: 'center',
                        display: 'flex'
                      }}>
                        <Button
                          color={'primary'}
                          sx={{ width: { xs: '100%', md: 'auto' } }}
                          variant="contained"
                          onClick={() => setViewAllResults(true)}
                        >
                          <FormattedMessage id={'modelPage.showAllModelsButton'} defaultMessage={'Show All Models'} />
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>

                      <Typography variant={'body1'}>
                        <FormattedMessage
                          id={'modelPage.modelList.allModelsExplainer'}
                          defaultMessage={'Below are the {model} models currently available. Tap on any model below to view more details, compare, or start building your deal now!'}
                          values={{ model: t(model.translatedName) }}
                        />
                      </Typography>

                      {hasSearchResults && (
                        <Box sx={{
                          justifyContent: 'center',
                          display: 'flex'
                        }}>
                          <Button
                            sx={{ width: { xs: '100%', md: 'auto' } }}
                            color={'primary'}
                            variant="contained"
                            onClick={() => setViewAllResults(false)}
                          >
                            <FormattedMessage id={'modelPage.showMatchingModelsButton'}
                              defaultMessage={'Show only matching models'} />
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                  <Box>
                    {rawTrimLevelYears.map(({ year, trimLevels }) => (
                      <Accordion
                        disableGutters
                        elevation={0} key={year} expanded={viewModelYears.indexOf(year) !== -1}
                        onChange={() => toggleModelYearDisplay(year)}>
                        <AccordionSummary
                          sx={{ backgroundColor: '#CBCBCB', fontSize: '1.4rem', borderRadius: '15px' }}
                          expandIcon={viewModelYears === year ? <ExpandLessIcon fontSize={'large'} /> :
                            <ExpandMoreIcon />}
                          aria-controls="panel1bh-content"
                          id={`${year}-header`}
                        >
                          <Typography variant={'h3'} sx={{ fontSize: '1.3rem' }}>
                            {intl.formatMessage({
                              id: 'modelYearSection.title',
                              defaultMessage: '{modelYear} Model Year'
                            }, { modelYear: year })}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
                            {trimLevels?.sort((a, b) => {
                              const a_offers = (a.offers?.length > 0) ? 1 : 0;
                              const b_offers = (b.offers?.length > 0) ? 1 : 0;
                              const a_best = Math.min(a.offers.filter(({ purchaseAmount }) => purchaseAmount).map(a => a.purchaseAmount));
                              const b_best = Math.min(b.offers.filter(({ purchaseAmount }) => purchaseAmount).map(a => a.purchaseAmount));
                              return b_offers - a_offers || a_best - b_best;
                            })?.map(({ slug, images, translatedSingularName, offers }) => {

                              return (
                                <Grid key={slug} xs={12} md={6} lg={4} sx={{ display: 'flex' }}>
                                  <a id={slug} />
                                  <TrimCardFavorite
                                    cardProps={{ sx: { width: '100%' } }}
                                    slug={slug}
                                    images={useGatsbySortedImages({ images, translatedSingularName })}
                                    title={`${t(manufacturer.translatedName)} ${t(translatedSingularName)}`}
                                    link={cl({
                                      mfrSlug: manufacturer.slug,
                                      mdlSlug: model?.slug,
                                      trimSlug: slug,
                                      search: searchParamString
                                    })}
                                    linkText={intl.formatMessage(
                                      {
                                        id: 'modelPage.linkTitle',
                                        defaultMessage: 'View sub model details for {make} {model}'
                                      },
                                      {
                                        make: t(manufacturer.translatedName),
                                        model: t(translatedSingularName)
                                      }
                                    )}
                                    stockAvailability={(offers?.length || 0) < 1 ? 'NOT_AVAILABLE' : offers.sort((a, b) => a?.stockAvailabilityRank - b?.stockAvailabilityRank)[0]?.stockAvailability}
                                    monthlyPrice={offers.sort((a, b) => a?.monthlyAmount - b?.monthlyAmount)?.[0]?.monthlyAmount && parseFloat(offers.sort((a, b) => a?.monthlyAmount - b?.monthlyAmount)?.[0]?.monthlyAmount)}
                                    purchasePrice={offers.sort((a, b) => a?.purchaseAmount - b?.purchaseAmount)?.[0]?.purchaseAmount && parseFloat(offers.sort((a, b) => a?.purchaseAmount - b?.purchaseAmount)?.[0]?.purchaseAmount)}
                                    currency={offers.sort((a, b) => a?.monthlyAmount - b?.monthlyAmount)?.[0]?.currency}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                  <GetHelpPanel
                    offer={bestNew}
                    offerId={bestNew?.id}
                    modelId={model?.id}
                    providerId={bestNew?.provider?.id}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </IntlProvider>
      </DNGatsbyTemplate>
    </LocaleProvider>
  );
};


ModelPage.propTypes = {
  pageContext: PropTypes.shape({
    lang: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired
  }),
  data: PropTypes.shape({
    driveNinjaManufacturerType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string,
        en: PropTypes.string.isRequired
      }).isRequired,
      slug: PropTypes.string.isRequired
    }),

    allDriveNinjaTrimLevelType: PropTypes.shape({
      group: PropTypes.arrayOf(PropTypes.shape({
        edges: PropTypes.arrayOf(PropTypes.shape({
          node: PropTypes.shape({
            translatedSingularName: PropTypes.shape({
              ar: PropTypes.string,
              en: PropTypes.string.isRequired
            }).isRequired,
            slug: PropTypes.string.isRequired,
            images: PropTypes.arrayOf(PropTypes.shape({
              orderingPriority: PropTypes.number.isRequired,
              thumbnail: PropTypes.shape({}),
              regularImage: PropTypes.shape({}),
              fullImage: PropTypes.shape({})
            })).isRequired
          })
        }))
      }))
    }),
    driveNinjaModelType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string,
        en: PropTypes.string.isRequired
      }).isRequired,
      translatedDescription: PropTypes.shape({
        ar: PropTypes.string,
        en: PropTypes.string.isRequired
      }).isRequired,
      slug: PropTypes.string.isRequired,
      modelImages: PropTypes.arrayOf(PropTypes.shape({
        orderingPriority: PropTypes.number.isRequired,
        thumbnail: PropTypes.shape({}),
        regularImage: PropTypes.shape({}),
        fullImage: PropTypes.shape({})
      })).isRequired
    })

  })

};


export const query = graphql`

    query ($slug: String!, $mfrSlug: String!) {
        driveNinjaManufacturerType(slug: {eq: $mfrSlug}) {
            translatedName {
                en
                ar
            }
            slug
            id
        }
        allDriveNinjaTrimLevelType(
            filter: {subModel: {generation: {model: {slug: {eq:$slug}}}}}
            sort: {modelYear: DESC}
        ) {
            group(field: {modelYear: SELECT}) {
                edges {
                    node {
                        modelYear
                        slug
                        translatedSingularName {
                            ar
                            en
                        }
                        images {
                            orderingPriority
                            localImage {

                                publicURL
                                childImageSharp {
                                    gatsbyImageData(height: 200, layout: FIXED)
                                    id
                                }
                            }
                        }
                    }
                }
                field
                fieldValue
            }
        }

        allDriveNinjaModelType(filter: {slug: {eq: $slug}}) {
            edges {
                node {
                    manufacturer {
                        slug
                        translatedName {
                            ar
                            en
                        }
                    }
                    slug
                    translatedName {
                        ar
                        en
                    }
                    trimLevels {
                        edges {
                            node {
                                slug
                                translatedSingularName {
                                    ar
                                    en
                                }
                                carTypes {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                carShapes {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                drivingWheels {
                                    slug
                                }
                                lifestyles {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                gearbox {
                                    slug
                                }
                                fuelType {
                                    slug
                                }
                                soundSystemBrand {
                                    slug
                                }
                                images {
                                    orderingPriority
                                    localImage {
                                        publicURL
                                        childImageSharp {
                                            gatsbyImageData(height: 200, layout: FIXED)
                                            id
                                        }
                                    }
                                }
                                offers:newOffers {
                                    edges {
                                        node {
                                            id: remoteId
                                            interiorColor{
                                                genericColor{
                                                    slug
                                                }
                                            }
                                            exteriorColor{
                                                genericColor{
                                                    slug
                                                }
                                            }
                                            available
                                            currency
                                            purchaseAmount
                                            monthlyAmount
                                            exteriorColor {
                                                slug
                                            }
                                            interiorColor {
                                                slug
                                            }
                                            stockAvailability
                                            stockAvailabilityRank
                                            provider{
                                                id: remoteId
                                            }
                                        }
                                    }
                                }
                                adaptiveCruiseControl
                                androidAuto
                                appleCarPlay
                                automaticAirConditioning
                                automaticFoldingWingMirrors
                                automaticHeadlights
                                auxInput
                                blindSpotWarning
                                bluetooth
                                bootCapacity
                                camera360
                                chargeTime
                                cooledSeats
                                crashWarningSystem
                                cruiseControl
                                daytimeRunningLights
                                dealershipCode
                                dimensionHeight
                                dimensionLength
                                dimensionWidth
                                engineCapacity
                                electronicStabilityControl
                                electronicParkingBrake
                                electricWingMirrors
                                electricPassengerSeat
                                electricDriverSeat
                                frontFogLights
                                frontParkingSensors
                                fuelConsumption
                                fuelTankCapacity
                                hillStartAssist
                                hillDescentAssist
                                headlampsLed
                                headlampsLaser
                                headUpDisplay
                                handsFreeTailgate
                                isofixChildSeat
                                keylessEntry
                                laneDepartureAssist
                                leatherSeats
                                leatherWrappedSteeringWheel
                                modelYear
                                navigation
                                numAirbags
                                numCylinders
                                numDoors
                                numGears
                                numSeats
                                numSpeakers
                                paddleShifter
                                panoramicSunroof
                                power
                                powerToWeight
                                pushButtonStart
                                range
                                rearFogLights
                                rearParkingSensors
                                rearPassengerAcVents
                                rearWindscreenShade
                                remoteVehicleStart
                                reversingCamera
                                roofRackRails
                                safetyRating
                                searchRank
                                splitFoldingRearSeats
                                sportsSeats
                                sunroofFront
                                time0100
                                topSpeed
                                torque
                                touchscreen
                                tractionControlSystem
                                turningCircle
                                tyrePressureMonitoringSystem
                                updatedAt
                                usbInput
                                uuid
                                voiceControl
                                weight
                                wheelSize
                                wheelbase
                                wifiConnection
                                wirelessCharging
                            }
                        }
                    }
                }
            }
        }
        driveNinjaModelType(slug: {eq: $slug}) {
            translatedName {
                en
                ar
            }
            translatedDescription {
                en
                ar
            }
            slug
            id: remoteId
            modelImages {
                orderingPriority
                thumbnail: localImage {
                    childImageSharp {
                        gatsbyImageData(height: 100, width:100, layout: FIXED, transformOptions:{
                            fit:COVER,
                            cropFocus:CENTER
                        })
                    }
                }
                regularImage: localImage {
                    childImageSharp {
                        gatsbyImageData(width: 600, layout: FIXED)
                    }
                }
                fullImage: localImage {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }
        }
    }
`;

export const Head = ({ pageContext, data }) => {
  const { lang } = pageContext;
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const manufacturer = data.driveNinjaManufacturerType;
  const model = data.driveNinjaModelType;

  const t = useTranslation();
  return (
    <>
      <meta
        name="description"
        content={intl.formatMessage({
          id: 'ModelPage.meta.description',
          defaultMessage: 'Buy, Finance or Lease a new or used {manufacturer} {model}.  Save upto 15% off the list price.  Find the best deal now!'
        }, {
          manufacturer: t(manufacturer.translatedName),
          model: t(model.translatedName)
        })}
      />

      <meta
        name="og:description"
        content={intl.formatMessage({
          id: 'ModelPage.meta.description',
          defaultMessage: 'Buy, Finance or Lease a new or used {manufacturer} {model}.  Save upto 15% off the list price.  Find the best deal now!'
        }, {
          manufacturer: t(manufacturer.translatedName),
          model: t(model.translatedName)
        })}
      />

      <meta
        name="og:title"
        content={intl.formatMessage({
          id: 'ModelPage.meta.title',
          defaultMessage: 'The {manufacturer} {model}'
        }, {
          manufacturer: t(manufacturer.translatedName),
          model: t(model.translatedName)
        })}

      />
      <meta
        name="og:image"
        content={model?.images?.[0]?.publicURL}
      />

      <title>{intl.formatMessage({
        id: 'ModelPage.meta.title',
        defaultMessage: 'The {manufacturer} {model}'
      }, {
        manufacturer: t(manufacturer.translatedName),
        model: t(model.translatedName)
      })}</title>
    </>
  );
};


const PageWrapper = (props) => {
  return (
    <QueryParamProvider adapter={ReachAdapter}
    >
      <ModelPage {...props} />
    </QueryParamProvider>
  );
};
export default PageWrapper;

